<template>
  <v-navigation-drawer
    :model-value="open"
    :class="SIDEBAR_CLASS"
    data-locator="redaction-drawer"
    location="right"
    rail
    rail-width="0"
    permanent
    @transitionend="onTransitionEnd"
  >
    <div :class="{ 'd-flex flex-column align-center h-100' : !isPending }">
      <div class="d-flex align-center py-4 px-6 w-100">
        <template v-if="redactType">
          <app-button
            size="small"
            type="icon"
            class="mr-1"
            @click="goBack"
          >
            <app-material-icon name="keyboard_arrow_left" />
          </app-button>
          <h3 class="text-text-primary ml-2">
            <template v-if="redactType === REDACT_TYPE_OPTIONS.AI">
              {{ $t('common.PdfRedactingSidebar.redactWithProtege') }}
            </template>
            <template v-else-if="redactType === REDACT_TYPE_OPTIONS.KEYWORDS">
              {{ $t('common.PdfRedactingSidebar.redactByKeywords') }}
            </template>
            <template v-else-if="redactType === REDACT_TYPE_OPTIONS.ZONE">
              {{ $t('common.PdfRedactingSidebar.redactByZone') }}
            </template>
          </h3>
        </template>
        <h3 v-else class="text-text-primary">
          {{ $t('common.PdfRedactingSidebar.title') }}
        </h3>

        <a
          class="ml-auto mr-2 text-decoration-none text-text-link d-flex"
          :href="$t('common.PdfRedactingSidebar.urlHelpCenter')"
          target="_blank"
        >
          {{ $t('common.PdfRedactingSidebar.needHelp') }}
          <app-material-icon
            name="open_in_new"
            size="sm"
            color="icon-link"
            class="ml-1"
          />
        </a>
        <v-divider class="mx-2" vertical />

        <app-button
          data-locator="close-button"
          type="icon"
          size="small"
          @click="close"
        >
          <app-material-icon name="close" />
        </app-button>
      </div>

      <v-divider class="w-100" />

      <v-skeleton-loader v-if="isPending" class="pa-6" type="card" />
      <div
        v-else
        ref="sidebarScroll"
        class="d-flex flex-column pa-6 overflow-y-auto w-100"
      >
        <div v-if="!redactType">
          <p class="text-text-secondary" data-locator="redact-information">
            {{ $t('common.PdfRedactingSidebar.tutorial') }}
          </p>
          <PdfRedactingSidebarType
            class="mt-4"
            :default-redact-type="redactType"
            @change-redact-type="redactType = $event"
          />
        </div>

        <PdfRedactingSidebarOcrMessage
          v-if="shouldDisplayOcrMessage"
          class="mb-6"
          @launch-ocr-conversion="$emit('launch-ocr-conversion')"
        />

        <!-- AI -->
        <PdfRedactingSidebarContentAI
          v-show="redactType === REDACT_TYPE_OPTIONS.AI"
          class="redaction-panel"
          ref="redactSidebarContentAI"
          :mnemo="mnemo"
          :fileId="fileId"
          :pdfHasText="pdfHasText"
          :pdfAppInstance="pdfAppInstance"
          @updateRectangles="$emit('updateRectangles', $event)"
        />

        <!-- KEYWORDS -->
        <PdfRedactingSidebarContentKeyWords
          v-show="redactType === REDACT_TYPE_OPTIONS.KEYWORDS"
          class="redaction-panel"
          ref="redactSidebarContentKeywords"
          :pdfAppInstance="pdfAppInstance"
          :pdfHasText="pdfHasText"
          @updateRectangles="$emit('updateRectangles', $event)"
        />

        <!-- ZONE -->
        <PdfRedactingSidebarContentZone
          v-show="redactType === REDACT_TYPE_OPTIONS.ZONE"
          class="redaction-panel"
          :drawnRectangles="drawnRectangles"
          :activeRectangleId="activeRectangleId"
          :isRedactByZone="isRedactByZone"
          @update:isRedactByZone="isRedactByZoneComputed = $event"
          @set-active-rectangle-id="$emit('set-active-rectangle-id', $event)"
          @duplicate-on-all-pages="$emit('duplicate-on-all-pages', $event)"
          @delete-on-all-pages="$emit('delete-on-all-pages', $event)"
          @delete-rectangle="$emit('delete-rectangle', $event)"
        />
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { DARK_BLUE_500 } from '@/common/utils/colors'
import { REDACT_TYPE_OPTIONS } from '@/common/utils/redactTypes.ts'
import AppButton from '@/design-system/buttons/AppButton.vue'

import PdfRedactingSidebarContentAI from './PdfRedactingSidebarContentAI.vue'
import PdfRedactingSidebarContentKeyWords from './PdfRedactingSidebarContentKeyWords.vue'
import PdfRedactingSidebarContentZone from './PdfRedactingSidebarContentZone.vue'
import PdfRedactingSidebarOcrMessage from './PdfRedactingSidebarOcrMessage.vue'
import PdfRedactingSidebarType from './PdfRedactingSidebarType.vue'

const SIDEBAR_CLASS = 'right-sidebar-container'

export default {
  name: "PdfRedactingSidebar",
  components: {
    AppButton,
    PdfRedactingSidebarContentAI,
    PdfRedactingSidebarContentKeyWords,
    PdfRedactingSidebarContentZone,
    PdfRedactingSidebarOcrMessage,
    PdfRedactingSidebarType,
  },
  props: {
    pdfAppInstance: {
      required: true,
      type: [Object, null],
    },
    fileId: {
      type: String,
      required: true,
    },
    mnemo: {
      type: String,
      required: true,
    },
    open: {
      type: Boolean,
      default: false,
    },
    isPending: {
      type: Boolean,
      default: false,
    },
    /**
     * @model
     * A model to switch the redact by zone mode
     */
    isRedactByZone: {
      type: Boolean,
      default: false,
    },
    drawnRectangles: {
      type: Array,
      required: true,
    },
    activeRectangleId: {
      type: [Number, String, null],
      default: null,
    },
    pdfHasText : {
      type: Boolean,
      required: true,
    },
    displayOcrMessage : {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'close',
    'go-back',
    'update:isRedactByZone',
    'update:isRedactByKeywords',
    'update:isRedactByAI',
    'updateRectangles',
    'delete-rectangle',
    'fully-toggled',
    'set-active-rectangle-id',
    'duplicate-on-all-pages',
    'delete-on-all-pages',
    'launch-ocr-conversion',
  ],
  data () {
    return {
      redactType: null,
      REDACT_TYPE_OPTIONS,
      SIDEBAR_CLASS,
      DARK_BLUE_500,
    }
  },
  computed: {
    shouldDisplayOcrMessage () {
      return (
        (this.redactType === REDACT_TYPE_OPTIONS.AI ||
        this.redactType === REDACT_TYPE_OPTIONS.KEYWORDS)
        && this.displayOcrMessage
      )
    },
    isRedactByZoneComputed: {
      get () {
        return this.isRedactByZone
      },
      set (value) {
        this.$emit('update:isRedactByZone', value)
      },
    },
  },
  watch: {
    open (value) {
      if (!value) {
        this.redactType = null
        this.isRedactByZoneComputed = false
      }
    },
    redactType (value) {
      this.$emit('update:isRedactByKeywords', value === REDACT_TYPE_OPTIONS.KEYWORDS)
      this.$emit('update:isRedactByAI', value === REDACT_TYPE_OPTIONS.AI)
    },
  },
  methods: {
    goBack () {
      this.redactType = null
      this.isRedactByZoneComputed = false
    },
    close () {
      this.$emit('close')
    },
    onTransitionEnd (e) {
      if (e.target.classList.contains(SIDEBAR_CLASS)) {
        this.$emit('fully-toggled')
      }
    },
  },
}
</script>

<style scoped>
.redaction-panel {
  min-height: 0;
}
</style>
